<template>
  <div>
    <!-- Loading overlay while fetching full event details -->
    <loading-overlay v-if="loadingEventDetails" text="Event wird geladen..." />
    
    <!-- Permission Check: Nur für Admins und Manager (KEIN POS) -->
    <v-container fluid class="px-6 py-6" v-if="!hasGeneralAdminPermission">
      <permission-denied 
        message="Du benötigst Manager- oder Administrator-Rechte, um Events zu verwalten."
      />
    </v-container>
    
    <v-container fluid class="px-6 py-6" v-else-if="event && event._id">
      <v-row class="mb-5">
        <v-col cols="12">
          <!-- Archived Badge -->
          <v-alert
            v-if="event.archived"
            type="warning"
            dense
            colored-border
            border="left"
            elevation="2"
            class="mb-3"
          >
            <div class="d-flex align-center">
              <i class="material-icons-round me-2" style="font-size: 20px; color: #fb6340;">archive</i>
              <div>
                <p class="mb-0 font-weight-600" style="color: #344767;">Event ist archiviert</p>
                <p class="mb-0 text-caption" style="color: #67748e;">
                  Alle Bearbeitungsfunktionen sind deaktiviert.
                </p>
              </div>
            </div>
          </v-alert>

          <v-btn
            v-if="canEditEvent"
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-primary py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#5e72e4"
            small
            :disabled="event.archived"
            @click="
              $router.push('/admin/events/edit/' + event._id).catch(() => {})
            "
            >Event bearbeiten</v-btn
          >

          <v-btn
            v-if="canEditEvent"
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-default py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#344767"
            small
            @click="copyEvent"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">content_copy</i>
            Event kopieren
          </v-btn>
          
          <!-- Löschen Button (nur wenn Event löschbar ist) -->
          <v-btn
            v-if="!event.archived && canDeleteEvent && isEventDeletable"
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-error py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#f5365c"
            small
            @click="deleteEvent"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">delete</i>
            Event löschen
          </v-btn>
          
          <!-- Archivieren / Dearchivieren Button -->
          <v-btn
            v-if="canArchiveEvent && !event.archived && !isEventDeletable"
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-warning py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#fb6340"
            small
            @click="archiveEvent"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">archive</i>
            Event archivieren
          </v-btn>
          
          <v-btn
            v-if="canArchiveEvent && event.archived"
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-success py-2 px-6 me-2 mb-sm-0 mb-2"
            color="#2dce89"
            small
            @click="unarchiveEvent"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">unarchive</i>
            Event dearchivieren
          </v-btn>
        </v-col>
      </v-row>
      <v-row class="m-0">
        <v-col cols="12" class="pt-0 mb-10 mt-5">
          <v-card class="mb-30 card card-shadow border-radius-xl py-5">
            <div class="mt-n11 mx-4 card-header position-relative z-index-2">
              <div class="d-block blur-shadow-image">
                <img
                  v-if="event.img && !imageError"
                  :src="getImage(event.img)"
                  class="shadow border-radius-lg w-100"
                  style="height: 400px; object-fit: cover;"
                  :alt="event.img"
                  @error="handleImageError"
                />
                <div
                  v-else
                  class="d-flex align-items-center justify-content-center border-radius-lg shadow"
                  style="
                    height: 400px;
                    background: linear-gradient(135deg, #f6d365 0%, #fda085 100%);
                  "
                >
                  <i class="material-icons-round text-white" style="font-size: 120px; opacity: 0.5;">
                    image_not_supported
                  </i>
                </div>
              </div>
              <div
                v-if="event.img && !imageError"
                class="colored-shadow"
                v-bind:style="{
                  backgroundImage: 'url(' + getImage(event.img) + ')',
                }"
              ></div>
            </div>
            <div class="card-header-padding">
              <v-row>
                <v-col>
                  <p class="font-weight-600 text-h3 text-typo mb-0">
                    {{ event.title }} -
                    {{ event.date | moment("DD.MM.YYYY HH:mm") }} Uhr
                  </p>
                  <p v-if="event.lastEditBy && isAdmin" class="text-xs text-secondary mb-0 mt-1">
                    <v-icon x-small class="me-1">edit</v-icon>
                    Zuletzt bearbeitet von
                    <strong>{{ event.lastEditBy.firstname }} {{ event.lastEditBy.lastname }}</strong>
                    <span v-if="event.updated"> · {{ event.updated | moment("DD.MM.YYYY HH:mm") }}</span>
                  </p>
                </v-col>
              </v-row>
            </div>
          </v-card>
          <v-row>
            <v-col>
              <v-card
                class="px-4 py-4 card-shadow border-radius-xl h-100"
                :class="
                  event.approved
                    ? event.active
                      ? 'bg-gradient-success'
                      : 'bg-gradient-secondary'
                    : 'bg-gradient-warning'
                "
              >
                <p
                  v-if="!event.approved"
                  class="mb-4 mb-0 text-white font-weight-bold"
                >
                  Eventfreigabe durch Admin offen!
                </p>
                <div class="d-flex mb-4">
                  <p class="mb-0 text-white font-weight-light">
                    {{ event.active ? " Aktiv" : "Inaktiv" }}
                  </p>
                  <v-switch
                    :disabled="!hasAdminOrManagerPermission || (!event.approved && !event.active) || event.archived || loadingTables"
                    @change="updateEvent(event)"
                    :ripple="false"
                    class="d-inline-flex mt-0 pt-0 switch ms-auto"
                    v-model="event.active"
                    hide-details
                  ></v-switch>
                </div>

                <p class="mt-4 mb-0 text-white font-weight-bold">Eventstatus</p>
                <span class="text-xs text-white">{{
                  event.active
                    ? "Event ist sichtbar"
                    : "Event ist nicht sichtbar"
                }}</span>
              </v-card>
            </v-col>
            <v-col>
              <v-card
                class="px-4 py-4 card-shadow border-radius-xl h-100"
                :class="
                  event.approved
                    ? event.entries_active
                      ? 'bg-gradient-success'
                      : 'bg-gradient-secondary'
                    : 'bg-gradient-warning'
                "
              >
                <p
                  v-if="!event.approved"
                  class="mb-4 mb-0 text-white font-weight-bold"
                >
                  Eventfreigabe durch Admin offen!
                </p>
                <div class="d-flex mb-4">
                  <p class="mb-0 text-white font-weight-light">
                    {{ event.entries_active ? "Aktiv" : "Inaktiv" }}
                  </p>
                  <v-switch
                    :disabled="!hasAdminOrManagerPermission || (!event.approved && !event.entries_active) || event.archived || loadingTables"
                    @change="updateEvent(event)"
                    :ripple="false"
                    class="d-inline-flex mt-0 pt-0 switch ms-auto"
                    v-model="event.entries_active"
                    hide-details
                  ></v-switch>
                </div>

                <p class="mt-4 mb-0 text-white font-weight-bold">
                  Eventanmeldungen
                </p>
                <span class="text-xs text-white">{{
                  event.entries_active
                    ? "Anmeldungen sind möglich"
                    : "Anmeldungen sind nicht möglich"
                }}</span>
              </v-card>
            </v-col>
            <v-col sm="3" cols="12">
              <v-card
                class="mb-6 card-shadow border-radius-xl py-4 h-100 w-100"
              >
                <v-row no-gutters class="px-4">
                  <v-col sm="4">
                    <v-avatar
                      color="bg-gradient-primary border-radius-xl mt-n8"
                      class="shadow-primary"
                      height="64"
                      width="64"
                    >
                      <v-icon class="material-icons-round text-white" size="24"
                        >leaderboard</v-icon
                      >
                    </v-avatar>
                  </v-col>
                  <v-col sm="8" class="text-end">
                    <p
                      class="text-sm mb-0 text-capitalize text-body font-weight-light"
                    >
                      Beitrags Interaktionen
                    </p>
                    <h4 class="text-h4 text-typo font-weight-bolder mb-0">
                      {{ event.views }}
                    </h4>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
            <v-col sm="3" cols="12">
              <v-card
                class="mb-6 card-shadow border-radius-xl py-4 h-100 w-100"
              >
                <v-row no-gutters class="px-4">
                  <v-col sm="4">
                    <v-avatar
                      color="bg-gradient-info border-radius-xl mt-n8"
                      class="shadow-info"
                      height="64"
                      width="64"
                    >
                      <v-icon class="material-icons-round text-white" size="24"
                        >comments</v-icon
                      >
                    </v-avatar>
                  </v-col>
                  <v-col sm="8" class="text-end">
                    <p
                      class="text-sm mb-0 text-capitalize text-body font-weight-light"
                    >
                      Kommentare
                    </p>
                    <h4 class="text-h4 text-typo font-weight-bolder mb-0">
                      {{ eventComments.length }}
                    </h4>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mb-5">
            <v-col cols="12">
              <datatable-push-notifications :disabled="!hasAdminOrManagerPermission || event.archived"></datatable-push-notifications>
            </v-col>
          </v-row>

          <v-row class="mb-5">
            <v-col cols="12">
              <div style="position: relative; min-height: 200px;">
                <div
                  v-if="loadingTables"
                  style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(255, 255, 255, 0.8); z-index: 10; display: flex; align-items: center; justify-content: center; border-radius: 8px;"
                >
                  <div class="text-center">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="64"
                      width="6"
                    ></v-progress-circular>
                    <p class="mt-4 text-body-1 font-weight-medium">Daten werden aktualisiert...</p>
                  </div>
                </div>
                <datatable-search :loading="loadingTables" @entry-updating="handleEntryUpdating" @entry-updated="handleEntryUpdated"></datatable-search>
              </div>
            </v-col>
          </v-row>

          <v-row class="mb-5">
            <v-col cols="12">
              <div style="position: relative; min-height: 200px;">
                <div
                  v-if="loadingTables"
                  style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(255, 255, 255, 0.8); z-index: 10; display: flex; align-items: center; justify-content: center; border-radius: 8px;"
                >
                  <div class="text-center">
                    <v-progress-circular
                      indeterminate
                      color="primary"
                      size="64"
                      width="6"
                    ></v-progress-circular>
                    <p class="mt-4 text-body-1 font-weight-medium">Daten werden aktualisiert...</p>
                  </div>
                </div>
                <datatable-invoices :loading="loadingTables"></datatable-invoices>
              </div>
            </v-col>
          </v-row>

          <v-card class="card-shadow border-radius-xl mt-5">
            <v-card-text class="card-padding">
              <v-row class="mt-0 ml-1" justify="space-between">
                <h5 class="font-weight-bolder text-h5 text-typo mb-10">
                  {{ eventComments.length }} Kommentare
                </h5>
                <div class="mr-2">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        @click="getEventComments(event._id)"
                        v-bind="attrs"
                        v-on="on"
                        class="material-icons-round text-primary ms-auto"
                        size="30"
                      >
                        refresh
                      </v-icon>
                    </template>
                    <span>Aktualisieren</span>
                  </v-tooltip>
                </div>
              </v-row>

              <div v-if="eventComments.length == 0">
                <h5 class="text-h5 text-typo font-weight-bold mt-0 mb-2">
                  Keine Kommentare vorhanden
                </h5>
              </div>
              <div v-else>
                <div class="d-flex">
                  <div class="flex-grow-1 ms-4">
                    <div :key="index" v-for="(comment, index) in eventComments">
                      <v-list-item
                        :ripple="false"
                        class="px-0 border-radius-sm mb-2"
                      >
                        <v-avatar
                          width="48"
                          height="48"
                          class="shadow border-radius-lg me-4"
                        >
                          <img
                            v-if="comment.user.avatar"
                            :src="getImage(comment.user.avatar)"
                            alt="Avatar"
                            class="border-radius-lg"
                          />
                          <div v-else>
                            {{ comment.user.username.charAt(0)
                            }}{{ comment.user.username.charAt(1) }}
                          </div>
                        </v-avatar>
                        <v-list-item-content>
                          <div class="d-flex align-center">
                            <div>
                              <h6
                                class="mb-0 text-sm text-sm text-typo font-weight-bold"
                              >
                                {{ comment.user.username }}
                              </h6>
                              <p
                                class="mb-0 text-xs text-body font-weight-light"
                              >
                                {{ comment.comment }}
                              </p>
                            </div>
                            <div class="ms-auto">
                              <v-btn
                                @click="showDeleteDialog(comment)"
                                :ripple="false"
                                :disabled="event.archived"
                                small
                                text
                                width="auto"
                                class="pe-4 ps-0 mb-0 text-primary font-weight-bolder"
                              >
                                Löschen
                              </v-btn>
                            </div>
                          </div>
                        </v-list-item-content>
                      </v-list-item>
                      <p class="text-xs mt-2 font-weight-light text-body">
                        {{ comment.created | moment("DD.MM.YYYY HH:mm") }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-snackbar
        top
        v-model="snackbar.visible"
        :color="snackbar.color"
        class="snackbar-shadow"
      >
        <div class="d-flex align-start alert-notify">
          <v-icon size="24" class="text-white me-3 mt-1 material-icons-round"
            >notifications</v-icon
          >
          <p class="mb-0">
            <span class="font-size-root font-weight-600">{{
              snackbar.title
            }}</span>
            <br />
            {{ snackbar.message }}
          </p>
        </div>

        <template v-slot:action="{ attrs }">
          <v-btn
            icon
            elevation="0"
            max-width="136"
            :ripple="false"
            height="43"
            class="font-weight-600 text-capitalize py-3 px-6 rounded-sm"
            color="rgba(255,255,255, .85)"
            @click="snackbar.visible = false"
            v-bind="attrs"
          >
            <v-icon size="13">fas fa-times</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
    <delete-dialog
      :open="dialogDelete"
      @close="closeCommentDelete"
      @confirm="deleteCommentConfirm"
    ></delete-dialog>

    <!-- Merge-Konflikt Dialog -->
    <event-conflict-dialog
      :open="conflictDialogOpen"
      :conflictData="conflictData"
      :localData="conflictLocalData"
      @close="handleConflictClose"
      @use-server-version="handleConflictUseServer"
      @force-my-version="handleConflictForce"
    />

    <!-- Archive Confirmation Dialog -->
    <v-dialog v-model="dialogArchive" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 font-weight-bold text-typo pa-6">
          <i class="material-icons-round text-warning me-2" style="font-size: 28px;">archive</i>
          Event archivieren?
        </v-card-title>

        <v-card-text class="pa-6">
          <p class="text-body-1 text-typo mb-4">
            Bist Du sicher, dass Du dieses Event archivieren möchtest?
          </p>
          
          <v-alert type="warning" text dense class="mb-0">
            <p class="font-weight-600 mb-2 text-typo">Das Event wird archiviert und automatisch deaktiviert:</p>
            <ul class="pl-4 text-typo">
              <li>Keine neuen Anmeldungen mehr möglich</li>
              <li>Event ist nicht mehr öffentlich sichtbar</li>
              <li>Änderungen sind nicht mehr möglich</li>
            </ul>
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-secondary py-2 px-6 me-2"
            @click="closeArchiveDialog"
          >
            Abbrechen
          </v-btn>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-warning py-2 px-6"
            color="#fb6340"
            @click="confirmArchive"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">archive</i>
            Jetzt archivieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unarchive Confirmation Dialog -->
    <v-dialog v-model="dialogUnarchive" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 font-weight-bold text-typo pa-6">
          <i class="material-icons-round text-success me-2" style="font-size: 28px;">unarchive</i>
          Event dearchivieren?
        </v-card-title>

        <v-card-text class="pa-6">
          <p class="text-body-1 text-typo mb-4">
            Möchtest Du dieses Event wirklich dearchivieren?
          </p>
          
          <v-alert type="info" text dense class="mb-0">
            <p class="font-weight-600 mb-2 text-typo">Nach der Dearchivierung:</p>
            <ul class="pl-4 text-typo">
              <li>Event bleibt zunächst inaktiv</li>
              <li>Event und Anmeldungen müssen manuell aktiviert werden</li>
              <li>Bearbeitung ist wieder möglich</li>
            </ul>
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-secondary py-2 px-6 me-2"
            @click="closeUnarchiveDialog"
          >
            Abbrechen
          </v-btn>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-success py-2 px-6"
            color="#2dce89"
            @click="confirmUnarchive"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">unarchive</i>
            Dearchivieren
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="dialogDeleteEvent" max-width="600px" persistent>
      <v-card>
        <v-card-title class="text-h5 font-weight-bold text-typo pa-6">
          <i class="material-icons-round text-error me-2" style="font-size: 28px;">delete</i>
          Event endgültig löschen?
        </v-card-title>

        <v-card-text class="pa-6">
          <p class="text-body-1 text-typo mb-4">
            Sind Sie sicher, dass Sie dieses Event <strong>endgültig löschen</strong> möchten?
          </p>
          
          <v-alert type="error" text dense class="mb-0">
            <p class="font-weight-600 mb-2 text-typo">Diese Aktion kann nicht rückgängig gemacht werden!</p>
            <ul class="pl-4 text-typo">
              <li>Das Event wird vollständig aus der Datenbank entfernt</li>
              <li>Alle zugehörigen Daten gehen unwiderruflich verloren</li>
              <li>Das Event kann nicht wiederhergestellt werden</li>
            </ul>
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-secondary py-2 px-6 me-2"
            @click="closeDeleteEventDialog"
          >
            Abbrechen
          </v-btn>
          <v-btn
            elevation="0"
            height="43"
            class="font-weight-bold text-uppercase btn-default bg-gradient-error py-2 px-6"
            color="#f5365c"
            @click="confirmDeleteEvent"
          >
            <i class="material-icons-round me-2" style="font-size: 16px;">delete</i>
            Endgültig löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
// import CalendarWidget from "./Widgets/CalendarWidget.vue";
import { VueEditor } from "vue2-editor";

import DatePicker from "v-calendar/lib/components/date-picker.umd";
import Dropzone from "./Widgets/Dropzone.vue";
import coreData from "@/mixins/coreDataMixin";
import permissionMixin from "@/mixins/permissionMixin";
import DatatableSearch from "./Widgets/DatatableSearch.vue";
import DatatablePushNotifications from "./Widgets/DatatablePushNotifications.vue";
import DeleteDialog from "@/components/DeleteDialog.vue";
import DatatableInvoices from "./Widgets/DatatableInvoices.vue";
import PermissionDenied from "@/components/PermissionDenied.vue";
import EventConflictDialog from "./Widgets/EventConflictDialog.vue";
import axiosAuth from "@/shared/configs/axios-auth";
import { mapGetters } from 'vuex';

export default {
  name: "EventsDetail",
  mixins: [coreData, permissionMixin],
  components: {
    DeleteDialog,
    VueEditor,
    DatePicker,
    Dropzone,
    DatatableSearch,
    DatatablePushNotifications,
    DatatableInvoices,
    PermissionDenied,
    EventConflictDialog,
  },
  data: function () {
    return {
      dialogDelete: false,
      dialogDeleteComment: false,
      dialogDeleteEvent: false,
      dialogArchive: false,
      dialogUnarchive: false,
      editedItemDelete: {},
      imageError: false,
      loadingEventDetails: true, // Loading state for event details
      loadingTables: false, // Loading state for tables during updates
      isReloadingEvent: false, // Flag to prevent reload loop
      conflictDialogOpen: false,
      conflictData: null,
      conflictLocalData: null,
    };
  },
  watch: {
    event() {
      if (this.event) {
        this.getEventComments(this.event._id);
        // Reset image error when event changes
        this.imageError = false;
      }
    },
    'event.img'() {
      // Reset error state when image changes
      this.imageError = false;
    },
    async $route(to, from) {
      // Route hat sich geändert, neues Event laden
      const newEventId = to.params.id;
      const oldEventId = from.params.id;
      
      if (newEventId && newEventId !== oldEventId) {
        console.log('Route geändert, lade neues Event:', newEventId);
        try {
          this.loadingTables = true;
          await this.$store.dispatch("getEventByIdAdmin", newEventId);
        } catch (error) {
          console.error('Fehler beim Laden des Events:', error);
        } finally {
          this.loadingTables = false;
        }
      }
    },
  },
  async mounted() {
    // Direkter Aufruf: Event IMMER frisch vom Backend laden
    const eventId = this.$route.params.id;
    if (eventId) {
      try {
        this.loadingEventDetails = true;
        console.log('Lade Event vom Backend (immer frische Daten):', eventId);
        await this.$store.dispatch("getEventByIdAdmin", eventId);
        console.log('✅ Event vollständig geladen:', this.event);
      } catch (error) {
        console.error('Fehler beim Laden des Events:', error);
      } finally {
        this.loadingEventDetails = false;
      }
    }
    
    // NOTE: Fallback removed - EventDetails always loads by ID
    // The events list is not needed for detail view
  },
  computed: {
    ...mapGetters(['isAdmin', 'currentForManagerId', 'ownManagerId', 'updating']),
    
    // Override event from mixin to ensure we have full data
    event() {
      const eventId = this.$route.params.id;
      if (!eventId) return {};
      
      const foundEvent = this.$store.getters.events.find(el => el._id === eventId);
      
      // Check if event has full entries (Array) or optimized structure (Object with length)
      if (foundEvent && foundEvent.entries && Array.isArray(foundEvent.entries)) {
        return foundEvent;
      }
      
      // If entries is not an array, event is not fully loaded yet
      return this.loadingEventDetails ? {} : foundEvent || {};
    },
    
    canArchiveEvent() {
      // Nur Event-Owner (admin: true) können ihr eigenes Event archivieren
      // NICHT eingeladene Manager (POS/Checker)
      if (!this.event || !this.isAdmin) return false;
      
      // Prüfe ob es das eigene Event ist (Owner)
      const eventManagerId = this.event.manager._id || this.event.manager;
      return this.ownManagerId === eventManagerId;
    },
    canManageEvents() {
      const currentForManagerId = this.$store.getters.currentForManagerId;
      const canManage = currentForManagerId && this.$store.getters.canManageEvents(currentForManagerId);
      console.log('🔍 [canManageEvents]', {
        currentForManagerId,
        canManage
      });
      return canManage;
    },
    canEditEvent() {
      // Prüfe ob User Owner des Events ist oder canManage Berechtigung hat
      // POS-User sind AUSGESCHLOSSEN (nur Owner oder Manager mit canManage)
      if (!this.event || !this.event._id) {
        return false;
      }
      
      const eventManagerId = this.event.manager?._id || this.event.manager;
      const ownManagerId = this.$store.getters.ownManagerId;
      const isOwner = eventManagerId && ownManagerId && eventManagerId.toString() === ownManagerId.toString();
      
      // Owner kann immer bearbeiten (auch wenn im Kontext eines anderen Managers)
      if (isOwner && this.isAdmin) {
        return true;
      }
      
      // Prüfe ob User canManage Berechtigung hat (nicht nur canPos)
      // canManageEvents prüft bereits auf canManage, nicht auf canPos
      return this.canManageEvents;
    },
    canDeleteEvent() {
      // Prüfe ob User Owner des Events ist oder canManage Berechtigung hat
      if (!this.event || !this.event._id) {
        console.log('🔍 [canDeleteEvent] No event');
        return false;
      }
      
      const eventManagerId = this.event.manager?._id || this.event.manager;
      const ownManagerId = this.$store.getters.ownManagerId;
      const isOwner = eventManagerId && ownManagerId && eventManagerId.toString() === ownManagerId.toString();
      
      // Owner kann immer löschen (auch wenn im Kontext eines anderen Managers)
      if (isOwner && this.isAdmin) {
        console.log('✅ [canDeleteEvent] User is owner and admin');
        return true;
      }
      
      // Oder User hat canManage Berechtigung für den Event-Manager
      const canManage = this.canManageEvents;
      console.log('🔍 [canDeleteEvent]', {
        isOwner,
        isAdmin: this.isAdmin,
        canManage,
        eventManagerId,
        ownManagerId
      });
      
      return canManage;
    },
    isEventDeletable() {
      if (!this.event || !this.event._id) {
        console.log('🔍 [isEventDeletable] No event');
        return false;
      }
      
      console.log('🔍 [isEventDeletable] Checking event:', {
        _id: this.event._id,
        active: this.event.active,
        archived: this.event.archived,
        entries: this.event.entries,
        entriesLength: Array.isArray(this.event.entries) ? this.event.entries.length : (this.event.entries?.length || 0),
        canDeleteEvent: this.canDeleteEvent
      });
      
      // Event muss inaktiv sein
      if (this.event.active) {
        console.log('❌ [isEventDeletable] Event is active');
        return false;
      }
      
      // Keine Anmeldungen
      const entriesCount = Array.isArray(this.event.entries) 
        ? this.event.entries.length 
        : (this.event.entries?.length || 0);
      
      console.log('🔍 [isEventDeletable] Entries count:', entriesCount);
      
      if (entriesCount > 0) {
        console.log('❌ [isEventDeletable] Event has entries');
        return false;
      }
      
      console.log('✅ [isEventDeletable] Event is deletable!');
      return true;
    }
  },
  methods: {
    handleImageError() {
      this.imageError = true;
    },
    async updateEvent(forceOverwrite) {
      this.loadingTables = true;
      try {
        let data = {
          _id: this.event._id,
          active: this.event.active,
          entries_active: this.event.entries_active,
        };
        // Conflict-Check: updated-Timestamp mitsenden (außer bei Force-Overwrite)
        if (!forceOverwrite && this.event.updated) {
          data._lastKnownUpdate = this.event.updated;
        }
        await this.$store.dispatch("updateEvent", data);
        // Reload event to get fresh data
        await this.$store.dispatch("getEventByIdAdmin", this.event._id);
        this.SnackbarShow("Success", "Gespeichert!", "Eventstatus wurde aktualisiert.");
      } catch (error) {
        if (error.message === 'CONFLICT' && error.conflictData) {
          this.conflictData = error.conflictData;
          // Save user's intended switch values before reloading
          this.conflictLocalData = { ...this.event };
          // Reload to reset switches to actual server state
          await this.$store.dispatch("getEventByIdAdmin", this.event._id);
          this.conflictDialogOpen = true;
        } else {
          console.error('Error updating event:', error);
          this.SnackbarShow("Danger", "Fehler", "Eventstatus konnte nicht gespeichert werden.");
        }
      } finally {
        this.loadingTables = false;
      }
    },
    handleConflictClose() {
      // Switches already reflect server state (reloaded on conflict)
      this.conflictDialogOpen = false;
      this.conflictData = null;
      this.conflictLocalData = null;
    },
    async handleConflictUseServer() {
      // Switches already reflect server state (reloaded on conflict)
      this.conflictDialogOpen = false;
      this.SnackbarShow("Info", "Server-Version geladen", "Die aktuelle Server-Version wurde übernommen.");
      this.conflictData = null;
      this.conflictLocalData = null;
    },
    async handleConflictForce() {
      this.conflictDialogOpen = false;
      // Restore user's intended switch values before force-saving
      if (this.conflictLocalData) {
        this.event.active = this.conflictLocalData.active;
        this.event.entries_active = this.conflictLocalData.entries_active;
      }
      await this.updateEvent(true);
      this.conflictData = null;
      this.conflictLocalData = null;
    },
    copyEvent() {
      if (!this.event) return;
      // Sanitize event data for copying: exclude process-relevant fields
      const copyData = {
        title: '[KOPIE] ' + (this.event.title || ''),
        description: this.event.description,
        hints: this.event.hints,
        disclaimer: this.event.disclaimer,
        html: this.event.html,
        short: this.event.short,
        organizer: this.event.organizer,
        slots: this.event.slots,
        street: this.event.street,
        venue: this.event.venue,
        city: this.event.city,
        zipcode: this.event.zipcode,
        price: this.event.price,
        price_lowered: this.event.price_lowered,
        price_lowered_info: this.event.price_lowered_info,
        price_local: this.event.price_local,
        vat_value: this.event.vat_value,
        website: this.event.website,
        date: this.event.date ? new Date(this.event.date) : null,
        date_start: this.event.date_start ? new Date(this.event.date_start) : null,
        date_end: this.event.date_end ? new Date(this.event.date_end) : null,
        date_inlet: this.event.date_inlet ? new Date(this.event.date_inlet) : null,
        hide_ticket_amount: this.event.hide_ticket_amount,
        personalization_required: this.event.personalization_required,
        address_required: this.event.address_required,
        seat_type: this.event.seat_type,
      };
      this.$store.commit('setEventCopyData', copyData);
      this.$router.push('/admin/events/new/').catch(() => {});
    },
    handleEntryUpdating(isUpdating) {
      this.loadingTables = isUpdating;
    },
    async handleEntryUpdated() {
      // Reload event after entry update
      const eventId = this.$route.params.id;
      if (eventId) {
        try {
          await this.$store.dispatch("getEventByIdAdmin", eventId);
        } catch (error) {
          console.error('Fehler beim Neuladen des Events:', error);
        }
      }
    },
    
    archiveEvent() {
      this.dialogArchive = true;
    },

    async confirmArchive() {
      this.dialogArchive = false;

      try {
        this.$store.commit("updating", true);

        // PATCH Request mit archived: true (Backend setzt automatisch active: false und entries_active: false)
        await axiosAuth.patch(`/events/${this.event._id}`, {
          archived: true
        });

        this.SnackbarShow("Success", "Event archiviert!", "Das Event wurde archiviert und deaktiviert.");
        
        // Zurück zur Events-Liste
        this.$router.push('/admin/events');
      } catch (error) {
        console.error('Archive event error:', error);
        const message = (error.response && error.response.data && error.response.data.message) || 'Fehler beim Archivieren!';
        this.SnackbarShow("Danger", "Fehler", message);
      } finally {
        this.$store.commit("updating", false);
      }
    },

    closeArchiveDialog() {
      this.dialogArchive = false;
    },

    unarchiveEvent() {
      this.dialogUnarchive = true;
    },

    async confirmUnarchive() {
      this.dialogUnarchive = false;

      try {
        this.$store.commit("updating", true);

        // PATCH Request mit archived: false
        await axiosAuth.patch(`/events/${this.event._id}`, {
          archived: false,
          archivedAt: null
        });

        this.SnackbarShow("Success", "Event dearchiviert!", "Das Event ist nun wieder bearbeitbar.");
        
        // Event neu laden
        await this.$store.dispatch("getEventsAdmin");
      } catch (error) {
        console.error('Unarchive event error:', error);
        const message = (error.response && error.response.data && error.response.data.message) || 'Fehler beim Dearchivieren!';
        this.SnackbarShow("Danger", "Fehler", message);
      } finally {
        this.$store.commit("updating", false);
      }
    },

    closeUnarchiveDialog() {
      this.dialogUnarchive = false;
    },

    deleteEvent() {
      this.dialogDeleteEvent = true;
    },

    closeDeleteEventDialog() {
      this.dialogDeleteEvent = false;
    },

    async confirmDeleteEvent() {
      this.dialogDeleteEvent = false;

      try {
        this.$store.commit("updating", true);

        // DELETE Request - Backend prüft ob Event löschbar ist
        await axiosAuth.delete(`/events/${this.event._id}`);

        this.SnackbarShow("Success", "Event wurde gelöscht!", "Das Event wurde vollständig aus der Datenbank entfernt.");
        
        // Zurück zur Events-Liste
        this.$router.push('/admin/events').catch(() => {});
      } catch (error) {
        console.error('Delete event error:', error);
        const message = (error.response && error.response.data && error.response.data.message) || 'Fehler beim Löschen!';
        this.SnackbarShow("Danger", "Fehler", message);
      } finally {
        this.$store.commit("updating", false);
      }
    },
    
    showDeleteDialog(item) {
      this.editedItemDelete = item;
      this.dialogDelete = true;
    },

    showDeleteCommentDialog(item) {
      this.editedCommentDelete = item;
      this.dialogDelete = true;
    },

    deleteCommentConfirm() {
      this.deleteComment();
    },

    closeCommentDelete() {
      this.editedItemDelete = null;
      this.dialogDelete = false;
    },

    deleteComment() {
      if (!this.editedItemDelete) return;
      axiosAuth
        .delete("eventComments/" + this.editedItemDelete._id)
        .then(() => {
          this.uploadComplete = true;
          this.$store.commit("updating", false);
          this.$store.dispatch("getEventComments", this.event._id);
          this.SnackbarShow(
            "Success",
            "Aktion erfolgreich!",
            "Kommentar erfolgreich gelöscht!"
          );
          this.closeCommentDelete();
        })
        .catch((error) => {
          console.log(error);
          this.$store.commit("updating", false);
          this.SnackbarShow("Danger", "Fehler!");
        });
    },
  },
};
</script>
